import React from 'react';
import { Play, Square, MapPin, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useActiveShift, useToggleShift } from '@/hooks/api/useShifts';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { toast } from 'sonner';

export const ShiftControl = () => {
  const { data: activeShift, isLoading: isLoadingShift } = useActiveShift();
  const shiftStart = activeShift?.shiftStart;
  const isActive = activeShift?.status === 'ACTIVE';

  const { toggleShift: toggleShiftMutation, isToggling } = useToggleShift();
  const {
    isTracking,
    locationDeclined,
    error: locationError,
  } = useLocationTracking();

  const isLoading = isToggling || isLoadingShift;

  const formatFullTimestamp = (date: Date) =>
    date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

  const calculateElapsedTime = () => {
    if (!shiftStart) return '';
    const diff = Math.max(0, Date.now() - new Date(shiftStart).getTime());
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const handleToggleShift = () => {
    const wasActive = isActive;
    toggleShiftMutation(undefined, {
      onSuccess: (data) => {
        if (wasActive) {
          toast.success('Shift Ended', {
            description: (
              <div className="space-y-1 text-xs">
                <div>{formatFullTimestamp(new Date())}</div>
                <div className="text-muted-foreground">
                  Duration: {calculateElapsedTime()}
                </div>
              </div>
            ),
            icon: <Square className="h-4 w-4" />,
          });
        } else {
          const startedAt = data.shiftStart
            ? new Date(data.shiftStart)
            : new Date();
          toast.success('Shift Started', {
            description: formatFullTimestamp(startedAt),
            icon: <Play className="h-4 w-4" />,
          });
        }
      },
    });
  };

  if (isLoadingShift) {
    return (
      <div className="w-8 h-8 rounded-full animate-pulse bg-white/20 dark:bg-black/20" />
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'relative h-8 w-8 sm:w-auto sm:px-3 rounded-full sm:rounded-md',
            'hover:bg-white/20 dark:hover:bg-black/30 text-white dark:text-white',
            isActive && 'bg-white/10 dark:bg-black/20',
          )}
        >
          {/* Mobile */}

          <div className="sm:hidden relative">
            {isActive ? (
              <Square className="h-4 w-4 fill-current" />
            ) : (
              <Play className="h-4 w-4 fill-current" />
            )}
            <div
              className={cn(
                'absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-primary',
                isActive
                  ? locationDeclined
                    ? 'bg-amber-400'
                    : 'bg-emerald-500'
                  : 'bg-slate-400 dark:bg-slate-500',
              )}
            />
          </div>

          {/* Desktop */}
          {locationError && (
            <div style={{ color: 'red', fontSize: 12 }}>{locationError}</div>
          )}
          <div className="hidden sm:flex items-center gap-2">
            {isActive ? (
              <Square className="h-3.5 w-3.5 fill-current" />
            ) : (
              <Play className="h-3.5 w-3.5 fill-current" />
            )}
            <span className="text-sm font-medium">
              {isActive ? 'On Shift' : 'Start Shift'}
            </span>
            {isActive && (
              <div
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  locationDeclined
                    ? 'bg-amber-400' // ✅ amber pulse if location denied
                    : isTracking
                      ? 'bg-emerald-400 animate-pulse'
                      : 'bg-slate-400',
                )}
              />
            )}
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-80 p-4 bg-background border-border"
      >
        <div className="space-y-4">
          {/* Header */}
          <div className="space-y-1">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              {isActive ? (
                <>
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full',
                      locationDeclined
                        ? 'bg-amber-400'
                        : 'bg-emerald-500 animate-pulse',
                    )}
                  />
                  Shift Active
                </>
              ) : (
                'Start Your Shift'
              )}
            </h4>
            {isActive && shiftStart && (
              <p className="text-xs text-muted-foreground">
                Started: {formatFullTimestamp(new Date(shiftStart))}
              </p>
            )}
          </div>

          {/* Status Cards */}
          <div className="space-y-2">
            {/* ✅ Location declined warning */}
            {isActive && locationDeclined && (
              <div className="flex items-start gap-2 p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-xs">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                <div className="flex-1 space-y-0.5">
                  <div className="font-medium text-amber-700 dark:text-amber-300">
                    Location Not Being Tracked
                  </div>
                  <div className="text-amber-600 dark:text-amber-400">
                    Shift is active but your location isn't being shared. Enable
                    location access in your browser settings to resume tracking.
                  </div>
                </div>
              </div>
            )}

            {/* Location tracking — normal states */}
            {isActive && !locationDeclined && (
              <div
                className={cn(
                  'flex items-start gap-2 p-3 rounded-lg border text-xs',
                  isTracking
                    ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900'
                    : 'bg-amber-50  dark:bg-amber-950/20  border-amber-200  dark:border-amber-900',
                )}
              >
                <MapPin
                  className={cn(
                    'h-4 w-4 shrink-0 mt-0.5',
                    isTracking
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600  dark:text-amber-400',
                  )}
                />
                <div className="flex-1 space-y-0.5">
                  <div
                    className={cn(
                      'font-medium',
                      isTracking
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-amber-700  dark:text-amber-300',
                    )}
                  >
                    {isTracking
                      ? 'Location Tracking Active'
                      : 'Location Issues'}
                  </div>
                  <div
                    className={cn(
                      isTracking
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-amber-600  dark:text-amber-400',
                    )}
                  >
                    {isTracking
                      ? 'Your location is being shared'
                      : locationError || 'Waiting for GPS signal...'}
                  </div>
                </div>
              </div>
            )}

            {/* Not on shift info */}
            {!isActive && (
              <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/50 border-border text-xs">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                <div className="flex-1 space-y-0.5">
                  <div className="font-medium text-foreground">
                    Not on shift
                  </div>
                  <div className="text-muted-foreground">
                    Start your shift to receive tickets and enable location
                    tracking.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <Button
            onClick={handleToggleShift}
            disabled={isLoading}
            className={cn(
              'w-full gap-2 font-semibold',
              isActive
                ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                : 'bg-primary  hover:bg-primary/90  text-primary-foreground',
            )}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                {isActive ? 'Ending Shift...' : 'Starting Shift...'}
              </>
            ) : isActive ? (
              <>
                <Square className="w-4 h-4 fill-current" /> End Shift
              </>
            ) : (
              <>
                <Play className="w-4 h-4 fill-current" /> Start Shift
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
